import type { QuestionConfig } from "../FormFieldRenderer";
import type { ConditionalLogicConfig, ConditionalRuleGroup } from "@portal/app/_server/services/formFunnel/payloadTypes";

/**
 * Evaluates whether a question's conditional logic is satisfied given a dependent value.
 *
 * This is the single source of truth for conditional visibility evaluation,
 * used by both FormFunnelFlow (step-level visibility) and DynamicFormStep
 * (question-level visibility within a step).
 *
 * @param conditionalLogic - The question's conditional logic config
 * @param dependentValue - The current value of the question this depends on
 * @returns true if the question should be visible
 */
export function evaluateConditionalVisibility(
  conditionalLogic: QuestionConfig["conditionalLogic"],
  dependentValue: unknown
): boolean {
  if (!conditionalLogic || !conditionalLogic.enabled) return true;

  const { value, values, operator } = conditionalLogic;

  if (dependentValue === undefined || dependentValue === null || dependentValue === "") {
    return false;
  }

  // Handle array answers (e.g. from multiselect questions)
  if (Array.isArray(dependentValue)) {
    if (dependentValue.length === 0) return false;
    const answerArray = dependentValue.map(String);

    switch (operator) {
      case "equals":
        return typeof value === "string" && answerArray.includes(value);
      case "not_equals":
        return typeof value === "string" && !answerArray.includes(value);
      case "in":
        return Array.isArray(values) && answerArray.some((v) => values.includes(v));
      case "not_in":
        return Array.isArray(values) && answerArray.every((v) => !values.includes(v));
      case "contains":
        return (
          typeof value === "string" &&
          answerArray.some((a) => a.toLowerCase().includes(value.toLowerCase()))
        );
      default:
        return true;
    }
  }

  const answerStr = String(dependentValue);

  switch (operator) {
    case "equals":
      return dependentValue === value;
    case "not_equals":
      return dependentValue !== value;
    case "in":
      return Array.isArray(values) && values.includes(answerStr);
    case "not_in":
      return Array.isArray(values) && !values.includes(answerStr);
    case "contains":
      return (
        typeof value === "string" &&
        answerStr.toLowerCase().includes(value.toLowerCase())
      );
    default:
      return true;
  }
}

/**
 * Checks whether a question is visible given accumulated form answers.
 * Looks up the dependent value from the answers map and evaluates conditional logic.
 */
export function isQuestionVisible(
  q: QuestionConfig,
  answers: Record<string, unknown>
): boolean {
  if (!q.conditionalLogic?.dependsOn) return evaluateConditionalVisibility(q.conditionalLogic, undefined);
  return evaluateConditionalVisibility(q.conditionalLogic, answers[q.conditionalLogic.dependsOn]);
}

/**
 * Evaluates a group of conditional rules with AND/OR combinator logic.
 * Used for interstitial content page visibility.
 * Returns true (visible) when no rules are configured, preserving backward compatibility.
 *
 * Disabled or incomplete rules are filtered out before the combinator is applied — they
 * are non-constraining for AND (don't add false votes) AND for OR (don't short-circuit
 * to true). When every rule is filtered out, the group is treated as having no active
 * constraints and returns true.
 */
export function evaluateConditionalRules(
  ruleGroup: ConditionalRuleGroup | undefined,
  answers: Record<string, unknown>
): boolean {
  if (!ruleGroup?.enabled || !ruleGroup.rules?.length) return true;

  type ActiveRule = ConditionalLogicConfig & {
    dependsOn: string;
    operator: NonNullable<ConditionalLogicConfig["operator"]>;
    value: string;
  };

  const isActiveRule = (rule: ConditionalLogicConfig): rule is ActiveRule =>
    Boolean(rule.enabled && rule.dependsOn && rule.value != null && rule.operator);

  const activeRules = ruleGroup.rules.filter(isActiveRule);
  if (activeRules.length === 0) return true;

  const evaluateRule = (rule: ActiveRule): boolean =>
    evaluateConditionalVisibility(
      { enabled: true, dependsOn: rule.dependsOn, value: rule.value, operator: rule.operator },
      answers[rule.dependsOn]
    );

  return ruleGroup.combinator === "or"
    ? activeRules.some(evaluateRule)
    : activeRules.every(evaluateRule);
}
